const DB_NAME = "wallet-watch";
const DB_VERSION = 1;

const STORE_APP_META = "appMeta";
const STORE_APP_HANDLES = "appHandles";
const STORE_PROFILES = "profiles";
const STORE_PROFILE_SETTINGS = "profileSettings";
const STORE_YEARS = "years";
const STORE_BUDGET_CATEGORIES = "budgetCategories";
const STORE_EXPENSES = "expenses";
const PROFILE_BACKUP_FORMAT_VERSION = 1;

const CURRENT_YEAR = String(new Date().getFullYear());
const DEFAULT_PROFILE_ID = "profile-default";
const DEFAULT_YEAR_ID = `${DEFAULT_PROFILE_ID}:${CURRENT_YEAR}`;

let dbPromise;
const profileDataChangeListeners = new Set();

export function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed."));
  });
}

export function transactionDone(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed."));
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted."));
  });
}

export function emitProfileDataChanged(event) {
  if (!event?.profileId) {
    return;
  }

  profileDataChangeListeners.forEach((listener) => {
    try {
      listener(event);
    } catch (error) {
      console.error("Profile data change listener failed.", error);
    }
  });
}

export function subscribeProfileDataChanges(listener) {
  profileDataChangeListeners.add(listener);

  return () => {
    profileDataChangeListeners.delete(listener);
  };
}

export function openDatabase() {
  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      const transaction = request.transaction;

      if (!db.objectStoreNames.contains(STORE_APP_META)) {
        const appMeta = db.createObjectStore(STORE_APP_META, { keyPath: "key" });
        appMeta.createIndex("value", "value");
      }

      if (!db.objectStoreNames.contains(STORE_APP_HANDLES)) {
        db.createObjectStore(STORE_APP_HANDLES, { keyPath: "key" });
      }

      if (!db.objectStoreNames.contains(STORE_PROFILES)) {
        const profiles = db.createObjectStore(STORE_PROFILES, { keyPath: "id" });
        profiles.createIndex("isDefault", "isDefault");
        profiles.createIndex("archivedAt", "archivedAt");
      }

      if (!db.objectStoreNames.contains(STORE_PROFILE_SETTINGS)) {
        const profileSettings = db.createObjectStore(STORE_PROFILE_SETTINGS, { keyPath: "profileId" });
        profileSettings.createIndex("updatedAt", "updatedAt");
      }

      if (!db.objectStoreNames.contains(STORE_YEARS)) {
        const years = db.createObjectStore(STORE_YEARS, { keyPath: "id" });
        years.createIndex("profileId", "profileId");
        years.createIndex("profileYear", ["profileId", "year"], { unique: true });
      }

      if (!db.objectStoreNames.contains(STORE_BUDGET_CATEGORIES)) {
        const budgetCategories = db.createObjectStore(STORE_BUDGET_CATEGORIES, { keyPath: "id" });
        budgetCategories.createIndex("profileYear", ["profileId", "year"]);
        budgetCategories.createIndex("profileYearName", ["profileId", "year", "name"], { unique: true });
        budgetCategories.createIndex("archivedAt", "archivedAt");
      }

      if (!db.objectStoreNames.contains(STORE_EXPENSES)) {
        const expenses = db.createObjectStore(STORE_EXPENSES, { keyPath: "id" });
        expenses.createIndex("profileYear", ["profileId", "year"]);
        expenses.createIndex("categoryId", "categoryId");
        expenses.createIndex("date", "date");
      } else if (transaction) {
        const expenses = transaction.objectStore(STORE_EXPENSES);

        if (expenses.indexNames.contains("methodId")) {
          expenses.deleteIndex("methodId");
        }
      }

      if (db.objectStoreNames.contains("paymentMethods")) {
        db.deleteObjectStore("paymentMethods");
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Failed to open IndexedDB."));
  });

  return dbPromise;
}

export async function purgeWalletWatchDb() {
  const existingDb = dbPromise ? await dbPromise.catch(() => null) : null;
  existingDb?.close();
  dbPromise = null;

  await new Promise((resolve, reject) => {
    const request = window.indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error("Failed to delete IndexedDB."));
    request.onblocked = () => reject(new Error("Database deletion was blocked by another open connection."));
  });
}

export async function ensureSeedData() {
  const db = await openDatabase();
  const transaction = db.transaction(
    [
      STORE_APP_META,
      STORE_PROFILES,
      STORE_YEARS,
      STORE_BUDGET_CATEGORIES,
      STORE_EXPENSES
    ],
    "readwrite"
  );

  const profilesStore = transaction.objectStore(STORE_PROFILES);
  const existingProfileCount = await requestToPromise(profilesStore.count());

  if (existingProfileCount > 0) {
    await transactionDone(transaction);
    return;
  }

  const now = new Date().toISOString();

  transaction.objectStore(STORE_APP_META).put({ key: "activeProfileId", value: DEFAULT_PROFILE_ID });
  transaction.objectStore(STORE_APP_META).put({ key: "activeYear", value: CURRENT_YEAR });

  profilesStore.put({
    id: DEFAULT_PROFILE_ID,
    name: "Primary",
    source: "local",
    isDefault: true,
    archivedAt: null,
    createdAt: now,
    updatedAt: now
  });

  transaction.objectStore(STORE_YEARS).put({
    id: DEFAULT_YEAR_ID,
    profileId: DEFAULT_PROFILE_ID,
    year: CURRENT_YEAR,
    createdAt: now,
    updatedAt: now
  });

  await transactionDone(transaction);
}

export async function getMetaValue(key) {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_APP_META, "readonly");
  const record = await requestToPromise(transaction.objectStore(STORE_APP_META).get(key));
  await transactionDone(transaction);
  return record?.value ?? null;
}

export async function setMetaValues(entries) {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_APP_META, "readwrite");
  const store = transaction.objectStore(STORE_APP_META);

  entries.forEach(([key, value]) => {
    store.put({ key, value });
  });

  await transactionDone(transaction);
}

export async function deleteMetaValue(key) {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_APP_META, "readwrite");
  transaction.objectStore(STORE_APP_META).delete(key);
  await transactionDone(transaction);
}

export async function getHandleValue(key) {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_APP_HANDLES, "readonly");
  const record = await requestToPromise(transaction.objectStore(STORE_APP_HANDLES).get(key));
  await transactionDone(transaction);
  return record?.value ?? null;
}

export async function setHandleValue(key, value) {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_APP_HANDLES, "readwrite");
  transaction.objectStore(STORE_APP_HANDLES).put({ key, value });
  await transactionDone(transaction);
}

export async function deleteHandleValue(key) {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_APP_HANDLES, "readwrite");
  transaction.objectStore(STORE_APP_HANDLES).delete(key);
  await transactionDone(transaction);
}

export function readAllFromIndex(store, indexName, query) {
  return requestToPromise(store.index(indexName).getAll(query));
}

export function readAllFromStore(store) {
  return requestToPromise(store.getAll());
}

export async function countStore(store) {
  return requestToPromise(store.count());
}

export {
  CURRENT_YEAR,
  DB_NAME,
  DB_VERSION,
  DEFAULT_PROFILE_ID,
  PROFILE_BACKUP_FORMAT_VERSION,
  STORE_APP_META,
  STORE_BUDGET_CATEGORIES,
  STORE_EXPENSES,
  STORE_PROFILES,
  STORE_PROFILE_SETTINGS,
  STORE_YEARS
};
